import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { GatewayUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/outputs/gateway-user.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetUserQuery } from '@app/core/service/cpms-bo-user.service';
import { UserUpdateController } from '@app/feature/user/user-update/user-update.controller';
import { UserContextProvider } from '@app/feature/user/user.context';
import { useSetPageActionContext } from '@app/shared/layout/page-layout/page-layout-action.context-provider';
import { useSetPageTitleContext } from '@app/shared/layout/page-layout/page-layout-title.context-provider';

import { UserDetailsView } from './user-details.view';

interface UserDetailsControllerProps {
  resource: GatewayUserModel;
}

const UserDetailsController = (
  props: UserDetailsControllerProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { resource } = props;

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(`${resource.firstName} ${resource.lastName}`);
  }, [resource, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions: React.JSX.Element[] = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.USERS,
        action: ActionsByResource.USERS.UPDATE,
      })
    ) {
      actions.push(
        <ButtonIcon
          onClick={() => setActionUpdateIsDialogOpen(true)}
          icon="edit-05"
          key="update"
        />,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <UserContextProvider data={resource}>
      <UserDetailsView user={resource} />
      <Dialog
        onClose={() => setActionUpdateIsDialogOpen(false)}
        open={actionUpdateIsDialogOpen}
        confirmClose
        height="100%"
        size={DialogSize.LARGE}>
        <Dialog.Header
          title={t('$.user-update.title')}
          icon="map-01"
          onClose={() => setActionUpdateIsDialogOpen(false)}
        />
        <UserUpdateController
          onCancel={() => setActionUpdateIsDialogOpen(false)}
          onSuccess={() => setActionUpdateIsDialogOpen(false)}
        />
      </Dialog>
    </UserContextProvider>
  );
};

export const UserDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useCpmsBoGetUserQuery(params.id ?? '');

  const { Component } = withProtectedResource<
    GatewayUserModel,
    {},
    GatewayUserModel
  >(UserDetailsController, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.USERS,
        action: ActionsByResource.USERS.READ,
      }),
  })({});

  return Component;
};
